function getOptionalEnv<T extends unknown = string>(name: string, defaultValue?: T) {
  // in `vite.config.ts`, we manually define/override `process.env` to be the
  // calculated values from `scripts/configure-process-env.ts`.
  //
  // vite recommends using `import.meta.env`, however since we are grabbing all
  // available env values from the .env files in the aforementioned script, we
  // can simply use `process.env` directly.
  //
  // @see https://vitejs.dev/guide/env-and-mode.html
  // @see scripts/configure-process-env.ts
  return process.env[name] ?? defaultValue;
}

function getRequiredEnv<T extends unknown = string>(name: string, defaultValue?: T) {
  const env = getOptionalEnv(name, defaultValue);
  if (typeof env === 'undefined') {
    throw new Error(`Required environment variable not found: ${name}`);
  }
  return env;
}

export const APP_NAME = getRequiredEnv('APP_NAME', 'horizon');
export const AUTH0_CLIENT_ID = getRequiredEnv('AUTH0_CLIENT_ID');
export const AUTH0_DOMAIN = getRequiredEnv('AUTH0_DOMAIN');
export const CI_MERGE_REQUEST_IID = getOptionalEnv('CI_MERGE_REQUEST_IID');
export const CI_PROJECT_ID = getOptionalEnv('CI_PROJECT_ID');
export const CI_PROJECT_PATH = getOptionalEnv('CI_PROJECT_PATH');
export const DEPLOY_ENV = getRequiredEnv('DEPLOY_ENV');
export const NODE_ENV = getOptionalEnv('NODE_ENV', 'stage');
export const API_HOSTNAME = getRequiredEnv('API_HOSTNAME');
export const ATLAS_API_HOSTNAME = getRequiredEnv('ATLAS_API_HOSTNAME');
export const AWS_REGION = getRequiredEnv('AWS_REGION');
export const HORIZON_ENDPOINT = getRequiredEnv('HORIZON_ENDPOINT');
export const INGEST_SERVICE_HOSTNAME = getRequiredEnv('INGEST_SERVICE_HOSTNAME');
export const MEDIA_SERVICE_HOSTNAME = getRequiredEnv('MEDIA_SERVICE_HOSTNAME');
export const OPS_API_HOSTNAME = getOptionalEnv('OPS_API_HOSTNAME');
export const MAINTENANCE_MODE_ENABLED = getOptionalEnv('MAINTENANCE_MODE_ENABLED') === 'true';
export const ENABLE_SERVICE_WORKERS = getOptionalEnv('ENABLE_SERVICE_WORKERS') === 'true';
export const PUBLIC_API_DOMAIN_NAME = getRequiredEnv('PUBLIC_API_DOMAIN_NAME');
export const isLocal = /^(localhost|127\.0\.0\.1)/.test(window.location.host);
export const isTest = NODE_ENV === 'test';
export const isLocalDocker = /host\.docker\.internal:3000/.test(window.location.host);

const isPlaywright = !!(window as any)?.__E2E_TEST__;
export const isE2ETest = isPlaywright;

export const isStageReview = /^horizon-\d+\.stage\.skyspecs\.com$/.test(window.location.host);
// supports the deprecated (stage.horizon.) and the current (horizon.stage.)
// portal formats
export const isStage = /^(stage\.horizon|horizon\.stage)\.skyspecs\.com$/.test(
  window.location.host
);

export const isProdReview = /^horizon-\d+\.skyspecs\.com$/.test(window.location.host);
export const isProd = !isLocal && !isStage && !isStageReview && !isProdReview && !isLocalDocker;

export const APP_VERSION = isLocal ? 'local' : SKYBAN_VERSION.version;
